import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Languages, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AdTranslationsFormProps {
  titleTranslations: Record<string, string>;
  descriptionTranslations: Record<string, string>;
  onTitleChange: (translations: Record<string, string>) => void;
  onDescriptionChange: (translations: Record<string, string>) => void;
  adId?: string;
}

const LANGUAGE_FLAGS = {
  'fr': '🇫🇷',
  'en': '🇬🇧'
};

const LANGUAGE_NAMES = {
  'fr': 'Français',
  'en': 'English'
};

export const AdTranslationsForm = ({
  titleTranslations,
  descriptionTranslations,
  onTitleChange,
  onDescriptionChange,
  adId
}: AdTranslationsFormProps) => {
  const { toast } = useToast();
  const [currentLang, setCurrentLang] = useState<'fr' | 'en'>('fr');
  const [localTitle, setLocalTitle] = useState<Record<string, string>>(titleTranslations);
  const [localDescription, setLocalDescription] = useState<Record<string, string>>(descriptionTranslations);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    setLocalTitle(titleTranslations);
    setLocalDescription(descriptionTranslations);
  }, [titleTranslations, descriptionTranslations]);

  const handleTitleChange = (lang: string, value: string) => {
    const updated = { ...localTitle, [lang]: value };
    setLocalTitle(updated);
    onTitleChange(updated);
  };

  const handleDescriptionChange = (lang: string, value: string) => {
    const updated = { ...localDescription, [lang]: value };
    setLocalDescription(updated);
    onDescriptionChange(updated);
  };

  const handleAutoTranslate = async () => {
    const frenchTitle = localTitle['fr'];
    const frenchDescription = localDescription['fr'];

    if (!frenchTitle && !frenchDescription) {
      toast({
        title: "Aucun texte à traduire",
        description: "Veuillez d'abord remplir le titre ou la description en français",
        variant: "destructive"
      });
      return;
    }

    setTranslating(true);
    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: {
          text: frenchTitle,
          description: frenchDescription,
          targetLanguage: 'en',
          context: 'advertisement'
        }
      });

      if (error) throw error;

      if (data?.translatedTitle) {
        handleTitleChange('en', data.translatedTitle);
      }
      if (data?.translatedDescription) {
        handleDescriptionChange('en', data.translatedDescription);
      }

      toast({
        title: "Traduction réussie",
        description: "Le contenu a été traduit en anglais"
      });
    } catch (error) {
      console.error('Erreur traduction:', error);
      toast({
        title: "Erreur de traduction",
        description: "Impossible de traduire automatiquement. Veuillez traduire manuellement.",
        variant: "destructive"
      });
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Languages className="h-5 w-5 text-primary" />
        <Label className="text-base font-semibold">Informations multilingues</Label>
      </div>
      
      <Tabs value={currentLang} onValueChange={(v) => setCurrentLang(v as 'fr' | 'en')} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          {Object.entries(LANGUAGE_FLAGS).map(([lang, flag]) => (
            <TabsTrigger key={lang} value={lang} className="gap-2">
              <span>{flag}</span>
              <span>{LANGUAGE_NAMES[lang as keyof typeof LANGUAGE_NAMES]}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(LANGUAGE_FLAGS).map((lang) => (
          <TabsContent key={lang} value={lang} className="space-y-4 mt-4">
            <div>
              <Label htmlFor={`title-${lang}`}>
                Titre {lang === 'fr' ? '(source)' : ''}
              </Label>
              <Input
                id={`title-${lang}`}
                value={localTitle[lang] || ''}
                onChange={(e) => handleTitleChange(lang, e.target.value)}
                placeholder={`Titre en ${LANGUAGE_NAMES[lang as keyof typeof LANGUAGE_NAMES]}`}
                required={lang === 'fr'}
              />
            </div>

            <div>
              <Label htmlFor={`description-${lang}`}>
                Description {lang === 'fr' ? '(source)' : '(optionnel)'}
              </Label>
              <Textarea
                id={`description-${lang}`}
                value={localDescription[lang] || ''}
                onChange={(e) => handleDescriptionChange(lang, e.target.value)}
                placeholder={`Description en ${LANGUAGE_NAMES[lang as keyof typeof LANGUAGE_NAMES]}`}
                rows={3}
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-muted-foreground">
          💡 Le français est la langue source. Traduction anglaise optionnelle.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAutoTranslate}
          disabled={translating || !localTitle['fr']}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {translating ? 'Traduction...' : 'Auto-traduire en anglais'}
        </Button>
      </div>
    </div>
  );
};
